import { useSyncExternalStore } from 'react'

/** which sidebar groups are open, kept outside react.
 *
 *  every page renders its own AppShell, so the shell unmounts on each
 *  navigation and useState here would reset with it. a group has to stay open
 *  until the user closes it, which means the state has to outlive the
 *  component. module scope, so it also resets on a full reload.
 *
 *  two sets rather than one: `opened` is an explicit open, `closed` an explicit
 *  close, and a group nobody has touched falls back to "open if you are inside
 *  it". one set could not tell "never touched" from "closed".
 *
 *  that fallback is a seed, not a rule: the shell pins an entered group into
 *  `opened` so leaving it does not collapse it. only a click does.
 */
const opened = new Set<string>()
const closed = new Set<string>()

const listeners = new Set<() => void>()
/** useSyncExternalStore compares snapshots by identity, so mutating the sets
 *  needs a version counter to signal a change. */
let version = 0

function emit(): void {
  version += 1
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function isNavGroupOpen(href: string, isInside: boolean): boolean {
  if (opened.has(href)) return true
  if (closed.has(href)) return false
  return isInside
}

/** pins a group the user arrived inside without clicking it (a redirect, the
 *  palette, a deep link). without this it is open by inference only, so the
 *  next navigation elsewhere silently collapses it. an explicit close still
 *  wins: only a click reopens what a click closed. */
export function markNavGroupOpen(href: string): void {
  if (opened.has(href) || closed.has(href)) return
  opened.add(href)
  emit()
}

/** entering a group from outside always opens it: the click that takes you in
 *  should never leave you on a collapsed group. inside, it toggles. */
export function toggleNavGroup(href: string, isInside: boolean): void {
  const open = isNavGroupOpen(href, isInside)
  if (isInside && open) {
    closed.add(href)
    opened.delete(href)
  } else {
    opened.add(href)
    closed.delete(href)
  }
  emit()
}

export function useNavGroupVersion(): number {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => version
  )
}
